/**
 * UpgradeBanner - Shows upgrade prompts for BASIC tier users
 * 
 * Two modes:
 * 1. Inline banner - Shows in dashboard/settings
 * 2. Modal - Shows when clicking a gated feature
 * 
 * Pricing: $99 AUD one-time purchase
 */
import React, { useState } from 'react';
import { useFeatures } from '../hooks/useFeatures';
import { Button } from './ui/button';
import { X, Zap, Users, FileText, ClipboardCheck, Shield } from 'lucide-react';

// Feature descriptions for upsell
const PRO_FEATURES = [
  { id: 'multi_user', icon: Users, name: 'Multi-User Management', description: 'Add team members' },
  { id: 'audit_logs', icon: FileText, name: 'Audit Logs', description: 'Full compliance trail' },
  { id: 'inspections', icon: ClipboardCheck, name: 'Inspections', description: 'Scheduled safety inspections' },
  { id: 'advanced_reporting', icon: Shield, name: 'Advanced Reporting', description: 'Custom reports and analytics' },
];

/**
 * Inline upgrade banner for dashboard
 */
export function UpgradeBanner({ className = '', onUpgrade }) {
  const { tier, isTier } = useFeatures();
  const [dismissed, setDismissed] = useState(false);

  // Don't show for PRO+ or if dismissed
  if (isTier('PRO') || dismissed) return null;

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      // Dispatch custom event for App.js to handle
      window.dispatchEvent(new CustomEvent('navigate-billing'));
    }
  };

  return (
    <div className={`upgrade-banner ${className}`} data-testid="upgrade-banner">
      <div className="upgrade-banner-content">
        <div className="upgrade-banner-icon">
          <Zap size={24} />
        </div>
        <div className="upgrade-banner-text">
          <h4>Unlock More Features</h4>
          <p>Get multi-user management, audit logs, and advanced reporting.</p>
        </div>
        <div className="upgrade-banner-actions">
          <Button 
            variant="default" 
            size="sm" 
            onClick={handleUpgrade}
            data-testid="upgrade-btn"
          >
            Upgrade Now
          </Button>
          <button 
            className="upgrade-banner-dismiss" 
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Upgrade modal - shows when user tries to access a gated feature
 */
export function UpgradeModal({ isOpen, onClose, featureName, featureLabel, onUpgrade }) {
  const { tier } = useFeatures();

  if (!isOpen) return null;

  // Find the feature info
  const featureInfo = PRO_FEATURES.find(f => f.id === featureName);
  const Icon = featureInfo?.icon || Zap;

  const handleUpgrade = () => {
    onClose();
    if (onUpgrade) {
      onUpgrade();
    } else {
      window.dispatchEvent(new CustomEvent('navigate-billing'));
    }
  };

  return (
    <div className="upgrade-modal-overlay" onClick={onClose} data-testid="upgrade-modal">
      <div className="upgrade-modal" onClick={e => e.stopPropagation()}>
        <button className="upgrade-modal-close" onClick={onClose} aria-label="Close">
          <X size={20} />
        </button>
        
        <div className="upgrade-modal-header">
          <div className="upgrade-modal-icon">
            <Icon size={32} />
          </div>
          <h2>{featureLabel || 'Feature'} requires an upgrade</h2>
          <p>You're currently on the <strong>{tier || 'BASIC'}</strong> plan.</p>
        </div>

        <div className="upgrade-modal-features">
          <h3>Unlock these features:</h3>
          <ul>
            {PRO_FEATURES.map(feature => (
              <li key={feature.id} className={feature.id === featureName ? 'highlighted' : ''}>
                <feature.icon size={18} />
                <div>
                  <strong>{feature.name}</strong>
                  <span>{feature.description}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="upgrade-modal-pricing">
          <div className="price-tag">
            <span className="price">$99</span>
            <span className="period">AUD</span>
          </div>
          <p>One-time purchase • Own it forever</p>
        </div>

        <div className="upgrade-modal-actions">
          <Button 
            variant="default" 
            size="lg" 
            className="upgrade-modal-btn"
            onClick={handleUpgrade}
            data-testid="modal-upgrade-btn"
          >
            <Zap size={18} />
            Upgrade Now
          </Button>
          <button className="upgrade-modal-later" onClick={onClose}>
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to manage upgrade modal state
 */
export function useUpgradeModal() {
  const [modalState, setModalState] = useState({ isOpen: false, featureName: null, featureLabel: null });
  const { has } = useFeatures();

  const showUpgradeModal = (featureName, featureLabel) => {
    if (!has(featureName)) {
      setModalState({ isOpen: true, featureName, featureLabel });
      return true; // Blocked
    }
    return false; // Not blocked
  };

  const closeModal = () => {
    setModalState({ isOpen: false, featureName: null, featureLabel: null });
  };

  return {
    ...modalState,
    showUpgradeModal,
    closeModal,
  };
}

export default UpgradeBanner;
